import { toast } from "sonner"
import { filePutSignedUrl, useFilePutSignedUrl } from "@/api/generated/endpoints/api"
import { filePutSignedUrlBody } from "@/api/generated/schemas/api.zod"
import { getErrorMessage } from "@/api/instance"

export * from "./models"

export const fileApi = {
  // Raw API functions
  filePutSignedUrl,

  // Mutation Hooks
  useFilePutSignedUrl,
}

export const fileSchemas = {
  filePutSignedUrlBody,
}

/** アップロードを受け付けるファイル種別。file input の accept 属性に渡す */
export const UPLOAD_ACCEPT = filePutSignedUrlBody.shape.contentType.options.join(",")

export type UploadResult = {
  key: string // S3のパス（エンティティ作成時に使用）
  fileName: string // 元のファイル名
}

/**
 * ファイルをS3にアップロードする汎用関数
 * 1. 署名付きURL取得
 * 2. S3にPUTでアップロード
 */
export async function uploadFile(file: File): Promise<UploadResult> {
  // 1. 署名付きURL取得（許可外の種別はサーバーに送らず弾く）
  const body = filePutSignedUrlBody.safeParse({ contentType: file.type })
  if (!body.success) {
    throw new Error(`対応していないファイル形式です: ${file.name}`)
  }
  const { signedUrl } = await filePutSignedUrl(body.data)

  // 2. S3にアップロード
  const uploadResponse = await fetch(signedUrl.url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  })

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`)
  }

  return {
    key: signedUrl.key,
    fileName: file.name,
  }
}

/**
 * 複数ファイルをアップロード
 */
export async function uploadFiles(files: File[]): Promise<UploadResult[]> {
  try {
    return await Promise.all(files.map(uploadFile))
  } catch (error) {
    // ミューテーションを通らないので、共通のトーストに頼れない
    toast.error("ファイルのアップロードに失敗しました", { description: getErrorMessage(error) })
    throw error
  }
}
